import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  Flame, Plus, Play, CheckCircle, Clock,
  AlertTriangle, Eye
} from 'lucide-react'
import { getBiocharBatches } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { Role } from '../../lib/permissions'

export default function BiocharList({ ranchId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { role } = useAuth()
  // biochar_operator runs batches but does not create them -- batch
  // creation is a manager-side (or owner/system_admin) action via the
  // ASSIGN_BIOCHAR capability. Hide creation CTAs for operators so the
  // UI matches the backend gate.
  const canCreateBatch = role !== Role.BIOCHAR_OPERATOR
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 20

  const { data, isLoading, error } = useQuery({
    queryKey: ['biocharBatches', ranchId, statusFilter, page],
    queryFn: () => getBiocharBatches(ranchId, {
      status: statusFilter || undefined,
      page,
      page_size: pageSize,
    }),
  })

  const batches = data?.items || []
  const totalCount = data?.total || batches.length

  const getStatusBadge = (batch) => {
    const status = batch.status
    
    // Check for completed (backend uses 'completed', handle both)
    if (status === 'completed' || status === 'complete') {
      // Check if yield is abnormal
      if (batch.yield_pct && (batch.yield_pct < 18 || batch.yield_pct > 40)) {
        return { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'Flagged' }
      }
      return { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' }
    }
    
    if (status === 'in_progress') {
      return { color: 'bg-blue-100 text-blue-800', icon: Play, label: 'In Progress' }
    }
    
    if (status === 'assigned') {
      return { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Assigned' }
    }
    
    return { color: 'bg-gray-100 text-gray-800', icon: Clock, label: status || 'Unknown' }
  }

  const getYieldDisplay = (batch) => {
    if (!batch.yield_pct) return null
    
    const yieldPct = parseFloat(batch.yield_pct)
    let colorClass = 'text-green-600'
    let suffix = ' ✓'
    
    if (yieldPct < 18) {
      colorClass = 'text-red-600'
      suffix = ' ⚠️ Low'
    } else if (yieldPct > 40) {
      colorClass = 'text-red-600'
      suffix = ' ⚠️ High'
    }
    
    return (
      <span className={`font-medium ${colorClass}`}>
        {yieldPct.toFixed(1)}%{suffix}
      </span>
    )
  }

  const handleBatchAction = (batch) => {
    navigate(`/biochar/batches/${batch.id}/process`)
  }

  const getActionButton = (batch) => {
    const status = batch.status
    
    // Completed batches - view only
    if (status === 'completed' || status === 'complete' || batch.char_output_kg) {
      return (
        <button
          onClick={() => handleBatchAction(batch)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          <Eye className="h-4 w-4" />
          View
        </button>
      )
    }
    
    // Has biomass input - ready to complete
    if (batch.biomass_input_kg && batch.biomass_input_kg > 0) {
      return (
        <button
          onClick={() => handleBatchAction(batch)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
        >
          <CheckCircle className="h-4 w-4" />
          Complete
        </button>
      )
    }
    
    // Needs to start
    return (
      <button
        onClick={() => handleBatchAction(batch)}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
      >
        <Play className="h-4 w-4" />
        Start
      </button>
    )
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Error loading batches: {error.message}
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Biochar Batches</h1>
          <p className="text-gray-600">{totalCount} total batches</p>
        </div>
        {canCreateBatch && (
          <Link
            to="/biochar/new"
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus className="h-5 w-5" />
            Create Batch
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Status</option>
            <option value="assigned">Assigned (Pending Start)</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Batches List */}
      {isLoading ? (
        <div className="text-center py-12">Loading batches...</div>
      ) : batches.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Flame className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          {canCreateBatch ? (
            <>
              <p className="text-gray-500 mb-4">No biochar batches found</p>
              <Link
                to="/biochar/new"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-5 w-5" />
                Create First Batch
              </Link>
            </>
          ) : (
            <>
              <p className="text-gray-900 font-medium mb-1">No batches assigned yet</p>
              <p className="text-sm text-gray-500">
                Your manager will assign biochar batches here when they're ready to run.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Input (kg)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Output (kg)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Yield</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {batches.map((batch) => {
                const statusBadge = getStatusBadge(batch)
                const StatusIcon = statusBadge.icon
                
                return (
                  <tr key={batch.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{batch.batch_code}</div>
                      <div className="text-xs text-gray-500">{batch.kiln_type || 'No kiln set'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {batch.production_date 
                        ? new Date(batch.production_date).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {batch.biomass_input_kg ? `${batch.biomass_input_kg}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {batch.char_output_kg ? `${batch.char_output_kg}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getYieldDisplay(batch) || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {getActionButton(batch)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data?.pages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {page} of {data.pages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(data.pages, p + 1))}
            disabled={page === data.pages}
            className="px-4 py-2 border rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-1">
          <span className="text-green-600">✓ Normal yield:</span> 18-40%
        </div>
        <div className="flex items-center gap-1">
          <span className="text-red-600">⚠️ Flagged:</span> &lt;18% or &gt;40%
        </div>
      </div>
    </div>
  )
}
